import buildScreenHeaderItems from '@/components/buildScreenHeaderItems'
import { Center } from '@/components/ui/center'
import { Spinner } from '@/components/ui/spinner'
import useWebBackButton from '@/lib/hooks/useWebBackButton'
import FormCooperatorDetail from '@/modules/formSettings/FormCooperatorDetail'
import { selectFormBillingAccount } from '@/redux/slices/formBillingAccountSlice'
import {
  fetchFormCooperatorThunk,
  selectFormCooperatorById,
  ShallowFormCooperator,
} from '@/redux/slices/formCooperatorSlice'
import { fetchFormRolesThunk, selectFormRolesByFormToken } from '@/redux/slices/formRoleSlice'
import { fetchFormThunk, formSelectors } from '@/redux/slices/formSlice'
import { useAppDispatch, useAppSelector } from '@/redux/store'
import { Stack, useLocalSearchParams, useRouter } from 'expo-router'
import { useCallback, useEffect, useMemo } from 'react'

export default function FormCooperatorDetailPage() {
  const router = useRouter()
  const dispatch = useAppDispatch()
  const { formToken, id } = useLocalSearchParams<{ formToken: string; id: string }>()
  const headerLeftItems = useWebBackButton(`/forms/${formToken}/setting/cooperators` as any)

  const form = useAppSelector(state => formSelectors.selectById(state.forms, formToken))
  const isFormOwner = form?.owner?.id === id

  useEffect(() => {
    dispatch(fetchFormRolesThunk({ formToken, first: 100 }))
    if (!isFormOwner) {
      dispatch(fetchFormCooperatorThunk({ formToken, cooperatorId: id }))
    }
  }, [formToken, id, isFormOwner, dispatch])

  const currentUserId = useAppSelector(state => state.currentUser.user?.id)
  const storedCooperator = useAppSelector(state => selectFormCooperatorById(state, formToken, id))
  const formRoles = useAppSelector(state => selectFormRolesByFormToken(state, formToken))
  const formBillingAccount = useAppSelector(state => selectFormBillingAccount(state, formToken))
  const isExternal =
    formBillingAccount?.isOrgPlanOrTrial && storedCooperator?.billingAccount?.id !== formBillingAccount.id

  const avaliableFormRoles = useMemo(() => {
    return isExternal ? formRoles.filter(role => role.predefined) : formRoles
  }, [formRoles, isExternal])
  

  const cooperator = useMemo(() => {
    if (isFormOwner && form?.owner) {
      return {
        ...form.owner,
        formRole: { id: '' },
        formToken,
      } as ShallowFormCooperator
    }
    return storedCooperator
  }, [form, storedCooperator, isFormOwner, formToken])

  const handleRoleChangeSuccess = useCallback(
    (isCurrentUser: boolean) => {
      if (isCurrentUser) {
        dispatch(fetchFormThunk({ formToken }))
        router.dismissTo(`/forms/${formToken}`)
      }
    },
    [dispatch, formToken, router]
  )

  const handleRemoveSuccess = useCallback(
    (isCurrentUser: boolean) => {
      router.back()
      if (isCurrentUser) {
        router.dismissTo(`/forms/${formToken}`)
        if (router.canGoBack()) router.back()
      }
    },
    [formToken, router]
  )

  if (!cooperator || !formRoles.length || !currentUserId) {
    return (
      <Center className="h-[50vh]">
        <Spinner />
      </Center>
    )
  }

  return (
    <>
      <Stack.Screen
        options={{
          title: isFormOwner ? '表单创建者' : '协作者详情',
          ...buildScreenHeaderItems({ left: headerLeftItems }),
        }}
      />
      <FormCooperatorDetail
        cooperator={cooperator}
        roles={avaliableFormRoles}
        formToken={formToken}
        isFormOwner={isFormOwner}
        currentUserId={currentUserId}
        onRoleChangeSuccess={handleRoleChangeSuccess}
        onRemoveSuccess={handleRemoveSuccess}
      />
    </>
  )
}
